/*
 * Project: bellcloud
 * File: packages/utils/device/converters/formatCodec.ts
 * Author: SWAN
 * Created: 2025-11-03
 * Description: 원시 값과 포맷된 값 간의 변환을 담당하는 포맷 변환기
 */

import { SensorType, SENSOR_TYPE_INFO } from '../../../types/device/lite/sensors';
import { MiniSensorType, MINI_SENSOR_TYPE_INFO } from '../../../types/device/mini/sensors';
import { SENSOR_TO_CF_OPTIONS } from '../../../types/device/lite/cf_lel';
import {
  VOL_FOR_50_LEL_CH4_OPTIONS,
  VolFor50LelCh4,
} from '../../../types/device/lite/vol_for_50_lel_ch4';
import { GasConcUnit, GAS_CONC_UNIT_OPTIONS } from '../../../types/device/common/gas_conc_unit';
import { BASE_DATE, GAS_CONC_SCALE } from '../../../protocols/constants';
import { DeviceType, type ProtocolType } from '../../../types/device/common/deviceTypes';
// import { Error as LiteError, ERROR_OPTIONS } from '@/types/device/lite/error';
// import { Warning as LiteWarning, WARNING_OPTIONS } from '@/types/device/lite/warning';

/**
 * FormatConverter: 원시 값을 포맷된 값으로 변환 (또는 그 반대)
 *
 * 역할:
 * - Packet → JSON: raw value → formatted value (format 기반)
 * - JSON → Packet: formatted value → raw value (format 기반)
 *
 * 지원하는 포맷:
 * - Basic: bool, number, string
 * - Temporal: timestamp, date_from_basedate, date_with_sign
 * - Concentration: gas_concentration, array_conc
 * - Enum: enum_sensors, enum_array_sensors, enum_array_gas_conc_unit 등
 * - Special: version, mac_address, decimal_point_and_unit
 */
export class FormatConverter {
  /**
   * 원시 값을 포맷된 값으로 변환 (Packet → JSON)
   * @param rawValue 원시 값
   * @param format 포맷 문자열
   * @param protocol `DeviceType`과 동일 (`belllite` / `bellmini`)
   * @param sensorType 센서 타입 (enum 변환 시 필요)
   * @param debug 디버그 정보
   * @returns 포맷된 값
   */
  static applyFormat(
    rawValue: unknown,
    format: string,
    protocol?: ProtocolType,
    sensorType?: SensorType | SensorType[]
  ): unknown {
    // Basic formats
    if (format === 'bool') return this.toBool(rawValue);
    if (format === 'number') return this.toNumber(rawValue);
    if (format === 'string' || format === 'startup_message') return this.toString(rawValue);

    // Temporal formats
    if (format === 'timestamp') return this.toTimestamp(rawValue);
    if (format === 'date_from_basedate' || format === 'date') {
      return this.toDateFromBaseDate(rawValue);
    }
    if (format === 'date_seconds_from_basedate') {
      return this.toDateSecondsFromBaseDate(rawValue);
    }
    if (format === 'date_with_sign') return this.toNumber(rawValue);
    if (format === 'date_with_sign_from_base_date') {
      return this.toNumber(rawValue);
    }

    // Concentration formats
    if (format === 'gas_concentration') return this.toGasConcentration(rawValue);
    if (format === 'temperature') return this.toNumber(rawValue);

    // Array formats
    if (format === 'array_number') return this.toArrayNumber(rawValue);
    if (format === 'array_bool') return this.toArrayBool(rawValue);
    if (format === 'array_conc') return this.toArrayConc(rawValue);
    if (format === 'array_date_from_basedate') return this.toArrayDateFromBaseDate(rawValue);
    if (format === 'array_date_with_sign') return this.toArrayNumber(rawValue);

    // Enum formats
    if (format === 'enum_sensors') return this.toNumber(rawValue);
    if (format === 'enum_array_sensors') return this.toEnumArraySensors(rawValue, protocol);
    if (format === 'enum_array_gas_conc_unit') {
      return this.toEnumArrayGasConcUnit(rawValue, protocol);
    }
    if (format === 'enum_array_cf_lel') return this.toEnumArrayCfLel(rawValue, sensorType);
    if (format === 'enum_array_vol_for_50_lel_ch4') {
      return this.toEnumArrayVolFor50LelCh4(rawValue);
    }

    // Special formats
    if (format === 'version') return this.toVersion(rawValue);
    if (format === 'mac_address') return this.toMacAddress(rawValue);
    if (format === 'decimal_point_and_unit') return this.toDecimalPointAndUnit(rawValue);
    if (format === 'language_setting') return this.toNumber(rawValue);

    // Mini-specific enum formats
    if (format === 'enum_system_status') return this.toNumber(rawValue);
    if (format === 'enum_error_code' || format === 'enum_error') return this.toNumber(rawValue);
    if (format === 'enum_warning_code' || format === 'enum_warning') {
      return this.toNumber(rawValue);
    }

    // Array enum formats for error/warning codes
    if (format === 'enum_array_error') return this.toEnumArrayError(rawValue);
    if (format === 'enum_array_warning') return this.toEnumArrayWarning(rawValue);

    // Default: return as-is
    const result = rawValue;

    return result;
  }

  /**
   * 포맷된 값을 원시 값으로 변환 (JSON → Packet)
   * @param formattedValue 포맷된 값
   * @param format 포맷 문자열
   * @returns 원시 값
   */
  static parseFormat(formattedValue: unknown, format: string): unknown {
    // Basic formats
    if (format === 'bool') return formattedValue ? 1 : 0;
    if (format === 'number') return this.toNumber(formattedValue);
    if (format === 'string' || format === 'startup_message') {
      // 문자열을 UTF-8 바이트 배열로 변환 (TypeConverter가 패딩 처리)
      if (typeof formattedValue === 'string') {
        const encoder = new TextEncoder();
        return Array.from(encoder.encode(formattedValue));
      }
      return formattedValue;
    }

    // Temporal formats
    if (format === 'timestamp') return this.parseTimestamp(formattedValue);
    if (format === 'date_from_basedate' || format === 'date') {
      return this.parseDateFromBaseDate(formattedValue);
    }
    if (format === 'date_seconds_from_basedate') {
      return this.parseDateSecondsFromBaseDate(formattedValue);
    }
    if (format === 'date_with_sign') return this.parseDateWithSign(formattedValue);
    if (format === 'date_with_sign_from_base_date') {
      return this.parseDateWithSignFromBaseDate(formattedValue);
    }

    // Concentration formats
    if (format === 'gas_concentration') return this.parseGasConcentration(formattedValue);
    if (format === 'temperature') return this.toNumber(formattedValue);

    // Array formats
    if (format === 'array_number') return formattedValue; // Already array
    if (format === 'array_bool') {
      // Boolean 배열을 숫자 배열로 변환
      if (Array.isArray(formattedValue)) {
        return formattedValue.map(v => (v ? 1 : 0));
      }
      return formattedValue;
    }
    if (format === 'array_conc') return this.parseArrayConc(formattedValue);
    if (format === 'array_date_from_basedate') {
      return this.parseArrayDateFromBaseDate(formattedValue);
    }
    if (format === 'array_date_with_sign') return this.parseArrayDateWithSign(formattedValue);

    // Enum formats - extract value from {value, label}
    if (
      format === 'enum_sensors' ||
      format === 'enum_array_sensors' ||
      format === 'enum_array_gas_conc_unit' ||
      format === 'enum_array_cf_lel' ||
      format === 'enum_array_vol_for_50_lel_ch4'
    ) {
      return this.extractEnumValues(formattedValue);
    }

    // Array enum formats for error/warning - already numbers, return as-is
    if (format === 'enum_array_error' || format === 'enum_array_warning') {
      return formattedValue;
    }

    // Special formats
    if (format === 'version') return this.parseVersion(formattedValue);
    if (format === 'mac_address') return this.parseMacAddress(formattedValue);
    if (format === 'decimal_point_and_unit') return formattedValue;
    if (format === 'language_setting') return this.toNumber(formattedValue);

    // Default: return as-is
    const result = formattedValue;

    return result;
  }

  // ============ Basic Format Helpers ============

  private static toBool(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
    return false;
  }

  private static toNumber(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    if (typeof value === 'boolean') return value ? 1 : 0;
    return 0;
  }

  private static toString(value: unknown): string {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      // UTF-8 디코딩
      const bytes = (value as number[]).filter(b => b !== 0);
      if (bytes.length === 0) return '';
      try {
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(new Uint8Array(bytes));
      } catch {
        return bytes.map(b => String.fromCharCode(b)).join('');
      }
    }
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    return '';
  }

  // ============ Temporal Format Helpers ============

  /**
   * 문자열을 UTC 기준 밀리초로 파싱한다.
   *
   * 기기/서버 시각은 항상 UTC 기준("2023.01.01 UTC 이후 초")이다. 그러나
   * "YYYY-MM-DD HH:mm:ss"처럼 타임존 표기가 없는 문자열을 `new Date()`에 넣으면
   * 브라우저 로컬 시간으로 해석되어 tz 오프셋만큼 어긋난다. 이를 막기 위해
   * 시간 성분이 있고 tz 표기가 없으면 UTC(Z)로 간주해 파싱한다.
   * (날짜만 있는 "YYYY-MM-DD"는 new Date가 이미 UTC 자정으로 해석함)
   */
  private static stringToUtcMs(value: string): number {
    const s = value.trim();
    const hasTime = /\d{2}:\d{2}/.test(s);
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
    const normalized = hasTime && !hasTz ? `${s.replace(' ', 'T')}Z` : s;
    return new Date(normalized).getTime();
  }

  private static toTimestamp(value: unknown): string {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') {
      return new Date(BASE_DATE + value * 1000).toISOString();
    }
    if (value instanceof Date) return value.toISOString();
    return new Date().toISOString();
  }

  private static parseTimestamp(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      return Math.floor((this.stringToUtcMs(value) - BASE_DATE) / 1000);
    }
    return 0;
  }

  private static toDateFromBaseDate(value: unknown): string {
    if (typeof value === 'number') {
      const targetDate = new Date(BASE_DATE + value * 24 * 60 * 60 * 1000);
      return targetDate.toISOString().split('T')[0];
    }
    return '1970-01-01';
  }

  private static parseDateFromBaseDate(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      return Math.floor(
        (this.stringToUtcMs(value) - BASE_DATE) / (24 * 60 * 60 * 1000),
      );
    }
    return 0;
  }

  private static toDateSecondsFromBaseDate(value: unknown): string {
    if (typeof value === 'number') {
      const targetDate = new Date(BASE_DATE + value * 1000);
      return targetDate.toISOString().replace('T', ' ').split('.')[0];
    }
    return '1970-01-01 00:00:00';
  }

  private static parseDateSecondsFromBaseDate(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      return Math.floor((this.stringToUtcMs(value) - BASE_DATE) / 1000);
    }
    return 0;
  }

  private static parseDateWithSign(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const targetDate = new Date(value);
      const currentDate = new Date();
      return Math.floor((targetDate.getTime() - currentDate.getTime()) / (24 * 60 * 60 * 1000));
    }
    return 0;
  }

  private static parseDateWithSignFromBaseDate(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      return Math.floor(
        (this.stringToUtcMs(value) - BASE_DATE) / (24 * 60 * 60 * 1000),
      );
    }
    return 0;
  }

  // ============ Concentration Format Helpers ============

  private static toGasConcentration(value: unknown): number {
    if (typeof value === 'number') {
      return +(value / GAS_CONC_SCALE).toFixed(3);
    }
    return 0;
  }

  private static parseGasConcentration(value: unknown): number {
    if (typeof value === 'number') {
      return Math.round(value * GAS_CONC_SCALE);
    }
    return 0;
  }

  // ============ Array Format Helpers ============

  private static toArrayNumber(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value.map(v => this.toNumber(v));
    }
    return [];
  }

  private static toArrayBool(value: unknown): boolean[] {
    if (Array.isArray(value)) {
      return value.map(v => this.toBool(v));
    }
    return [];
  }

  private static toArrayConc(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value.map(v => this.toGasConcentration(v));
    }
    return [];
  }

  private static parseArrayConc(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value.map(v => this.parseGasConcentration(v));
    }
    return [];
  }

  private static toArrayDateFromBaseDate(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map(v => this.toDateFromBaseDate(v));
    }
    return [];
  }

  private static parseArrayDateFromBaseDate(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value.map(v => this.parseDateFromBaseDate(v));
    }
    return [];
  }

  private static parseArrayDateWithSign(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value.map(v => this.parseDateWithSignFromBaseDate(v));
    }
    return [];
  }

  // ============ Enum Format Helpers ============

  private static toEnumArraySensors(
    value: unknown,
    protocol?: ProtocolType
  ): Array<{ value: number; label: string }> {
    if (Array.isArray(value)) {
      return value.map(v => {
        if (typeof v === 'object' && v !== null && 'value' in v && 'label' in v) {
          return v as { value: number; label: string };
        }
        if (typeof v === 'number') {
          if (protocol === DeviceType.MINI) {
            const sensorInfo = MINI_SENSOR_TYPE_INFO[v as MiniSensorType];
            return { value: v, label: sensorInfo?.label ?? `Sensor ${v}` };
          } else {
            const sensorInfo = SENSOR_TYPE_INFO[v as SensorType];
            return { value: v, label: sensorInfo?.label ?? `Sensor ${v}` };
          }
        }
        return { value: 255, label: '' };
      });
    }
    return [];
  }

  private static toEnumArrayGasConcUnit(
    value: unknown,
    protocol?: ProtocolType
  ): Array<{ value: number; label: string }> {
    if (Array.isArray(value)) {
      return value.map(v => {
        if (typeof v === 'object' && v !== null && 'value' in v && 'label' in v) {
          return v as { value: number; label: string };
        }
        if (typeof v === 'number') {
          if (protocol === DeviceType.MINI) {
            const unitOption = GAS_CONC_UNIT_OPTIONS[v as GasConcUnit];
            return {
              value: v,
              label: unitOption?.label ?? GAS_CONC_UNIT_OPTIONS[GasConcUnit.PPM].label,
            };
          } else {
            const unitOption = GAS_CONC_UNIT_OPTIONS[v as GasConcUnit];
            return {
              value: v,
              label: unitOption?.label ?? GAS_CONC_UNIT_OPTIONS[GasConcUnit.PPM].label,
            };
          }
        }
        return { value: 0, label: 'PPM' };
      });
    }
    return [];
  }

  private static toEnumArrayCfLel(
    value: unknown,
    sensorType?: SensorType | SensorType[]
  ): Array<{ value: number; label: string }> {
    if (Array.isArray(value)) {
      return value.map((v, index) => {
        if (typeof v === 'object' && v !== null && 'value' in v && 'label' in v) {
          return v as { value: number; label: string };
        }
        if (typeof v === 'number') {
          let currentSensorType: SensorType | undefined;
          if (Array.isArray(sensorType)) {
            currentSensorType = sensorType[index];
          } else {
            currentSensorType = sensorType;
          }

          if (currentSensorType && SENSOR_TO_CF_OPTIONS[currentSensorType]) {
            const cfOption = SENSOR_TO_CF_OPTIONS[currentSensorType].find(
              option => option.value === v
            );
            if (cfOption) {
              return { value: v, label: cfOption.label };
            }
          }

          return { value: v, label: v === 0 ? 'Methane(1.0)' : `Gas ${v}` };
        }
        return { value: 0, label: 'Methane(1.0)' };
      });
    }
    return [];
  }

  private static toEnumArrayVolFor50LelCh4(
    value: unknown
  ): Array<{ value: number; label: string }> {
    if (Array.isArray(value)) {
      return value.map(v => {
        if (typeof v === 'object' && v !== null && 'value' in v && 'label' in v) {
          return v as { value: number; label: string };
        }
        if (typeof v === 'number') {
          return {
            value: v,
            label: VOL_FOR_50_LEL_CH4_OPTIONS[v as VolFor50LelCh4]?.label ?? 'Unknown',
          };
        }
        return {
          value: VolFor50LelCh4.US,
          label: VOL_FOR_50_LEL_CH4_OPTIONS[VolFor50LelCh4.US].label,
        };
      });
    }
    return [];
  }

  private static extractEnumValues(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(item => {
        if (typeof item === 'object' && item !== null && 'value' in item) {
          return (item as { value: number }).value;
        }
        return typeof item === 'number' ? item : 0;
      });
    }
    if (typeof value === 'object' && value !== null && 'value' in value) {
      return (value as { value: number }).value;
    }
    return value;
  }

  // ============ Special Format Helpers ============

  private static toVersion(value: unknown): string {
    if (Array.isArray(value)) {
      return 'v' + value.join('.');
    }
    return 'v0.0.0';
  }

  private static parseVersion(value: unknown): number[] {
    if (typeof value === 'string') {
      const parts = value.replace('v', '').split('.');
      return parts.map(p => parseInt(p, 10) || 0);
    }
    if (Array.isArray(value)) {
      return value;
    }
    return [0, 0, 0];
  }

  private static toMacAddress(value: unknown): string {
    if (Array.isArray(value)) {
      const bytes = value as number[];
      return bytes
        .map(b => b.toString(16).padStart(2, '0'))
        .join(':')
        .toUpperCase();
    }
    return '00:00:00:00:00:00';
  }

  private static parseMacAddress(value: unknown): number[] {
    if (typeof value === 'string') {
      return value.split(':').map(hex => parseInt(hex, 16) || 0);
    }
    if (Array.isArray(value)) {
      return value;
    }
    return [0, 0, 0, 0, 0, 0];
  }

  private static toDecimalPointAndUnit(value: unknown): { decimalPoint: number; unit: string } {
    if (typeof value === 'number') {
      const byte = value;
      const decimalPoint = byte & 0x0f;
      const unit = (byte >> 4) & 0x0f;
      return {
        decimalPoint,
        unit: GAS_CONC_UNIT_OPTIONS[unit as GasConcUnit]?.label ?? `Unknown(${unit})`,
      };
    }
    return { decimalPoint: 0, unit: 'PPM' };
  }

  // ============ Array Enum Helpers ============

  /**
   * Error 코드 배열을 변환 (Lite 디바이스용)
   * @param value 숫자 배열 [0, 1, 2, ...]
   * @returns 숫자 배열 (DB 저장용)
   */
  private static toEnumArrayError(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'number' ? v : 0));
    }
    return [];
  }

  /**
   * Warning 코드 배열을 변환 (Lite 디바이스용)
   * @param value 숫자 배열 [0, 1, 2, ...]
   * @returns 숫자 배열 (DB 저장용)
   */
  private static toEnumArrayWarning(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'number' ? v : 0));
    }
    return [];
  }
}
